import { useEffect, useState } from 'react';
import { Link2, X } from 'lucide-react';
import { Card, CardHeader } from '../_primitives';

export interface InternalLinkSuggestion {
  id: string;
  /** Anchor phrase that exists in the doc and would become the link text. */
  anchor: string;
  /** Title of the suggested target page. */
  target: string;
  /** Section heading where the anchor was found. */
  section?: string;
  /** Confidence 0–1. */
  confidence?: number;
  /** Resolved URL of the target. */
  url?: string;
}

export interface InternalLinksCardProps {
  links: InternalLinkSuggestion[];
  onRemove?: (link: InternalLinkSuggestion) => void;
}

export function InternalLinksCard({ links, onRemove }: InternalLinksCardProps) {
  // Track local removals only when the parent is not supplying a handler.
  const isControlled = onRemove != null;
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  // If the parent's list churns (new ids), reset local dismissals so we don't
  // hide brand-new suggestions just because they share an id with old ones.
  useEffect(() => {
    if (isControlled) return;
    setDismissed((prev) => {
      const next = new Set<string>();
      for (const id of prev) {
        if (links.some((l) => l.id === id)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [isControlled, links]);

  const visible = isControlled ? links : links.filter((l) => !dismissed.has(l.id));

  const dismissLocal = (link: InternalLinkSuggestion) =>
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(link.id);
      return next;
    });

  const handleRemove = (link: InternalLinkSuggestion) => {
    onRemove?.(link);
    if (!isControlled) dismissLocal(link);
  };

  return (
    <Card railSection="links">
      <CardHeader
        icon={<Link2 className="size-3.5" />}
        title="Internal links"
        trailing={
          <span className="text-[11px] text-muted-foreground">{visible.length} used</span>
        }
      />
      {/* Lists ALL internal client links used in this blog — no truncation;
          scrolls when the list is long. */}
      <div className="px-3 pb-3 flex flex-col gap-1.5 max-h-80 overflow-y-auto">
        {visible.map((link) => (
          <div
            key={link.id}
            className="rounded-md border border-border bg-warm-bg/50 px-2.5 py-2"
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span className="font-mono text-[10.5px] font-bold text-coral-dark bg-coral-light px-1.5 py-0.5 rounded">
                "{link.anchor}"
              </span>
              <div className="flex-1" />
              {link.confidence != null && (
                <span className="text-[10px] text-muted-foreground">
                  {Math.round(link.confidence * 100)}%
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                readOnly
                value={link.target}
                title={link.target}
                className="min-w-0 flex-1 rounded-md border border-border bg-card px-2 py-1 text-[11px] font-medium text-foreground outline-none"
              />
              <button
                type="button"
                onClick={() => handleRemove(link)}
                aria-label={`Remove internal link for ${link.anchor}`}
                className="inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <X className="size-3.5" />
              </button>
            </div>
            {link.section && (
              <div className="text-[10.5px] text-muted-foreground mt-1">in {link.section}</div>
            )}
          </div>
        ))}
        {visible.length === 0 && (
          <div className="text-center text-[12px] text-muted-foreground py-4">
            No internal links in this blog.
          </div>
        )}
      </div>
    </Card>
  );
}
